import type { SiteConfig } from '../../src/site/types';

/** chinasnowwatch.com — written for visitors outside mainland China. */
const config: SiteConfig = {
  id: 'cn',
  brand: {
    name: 'ChinaSnowWatch',
    shortName: 'ChinaSnow',
    logoText: { lead: 'ChinaSnow', accent: 'Watch' },
    alternateNames: ['China Snow Watch'],
    description: {
      en: 'Snow forecasts, satellite snow cover and trip guides for Chinese ski resorts.',
      ja: '中国のスキー場の降雪予報、衛星積雪画像、旅行ガイド。',
      ko: '중국 스키장의 강설 예보, 위성 적설 지도, 여행 가이드.',
      zh: '中国滑雪场的降雪预报、卫星积雪图和旅行指南。',
    },
    sameAs: [],
    logoPath: '/favicon-192.png',
    logoSize: 192,
    ogImagePath: '/og-image.jpg',
    contactEmail: 'hello@chinasnowwatch.com',
  },
  domain: 'chinasnowwatch.com',
  origin: 'https://chinasnowwatch.com',
  languages: ['en', 'zh', 'ko', 'ja'],
  crawlerLinks: true,
  crawlerResortDirectory: true,
  ogLocales: { en: 'en_GB', ja: 'ja_JP', ko: 'ko_KR', zh: 'zh_CN' },
  currency: { code: 'CNY', symbol: '¥', decimals: 0 },
  timezone: 'Asia/Shanghai',
  timezoneLabel: 'CST',
  utcOffsetHours: 8,
  country: {
    code: 'CN',
    name: { en: 'China', ja: '中国', ko: '중국', zh: '中国' },
    adjective: { en: 'Chinese', ja: '中国の', ko: '중국', zh: '中国' },
  },
  regions: [
    { id: 'Heilongjiang', name: { en: 'Heilongjiang', ja: '黒竜江省', ko: '헤이룽장성', zh: '黑龙江省' }, center: [45.8, 128.6] },
    { id: 'Jilin', name: { en: 'Jilin', ja: '吉林省', ko: '지린성', zh: '吉林省' }, center: [43.0, 126.5] },
    { id: 'Liaoning', name: { en: 'Liaoning', ja: '遼寧省', ko: '랴오닝성', zh: '辽宁省' }, center: [41.3, 123.4] },
    { id: 'Hebei', name: { en: 'Hebei', ja: '河北省', ko: '허베이성', zh: '河北省' }, center: [40.9, 115.4] },
    { id: 'Beijing', name: { en: 'Beijing', ja: '北京市', ko: '베이징시', zh: '北京市' }, center: [40.3, 116.4] },
    { id: 'Tianjin', name: { en: 'Tianjin', ja: '天津市', ko: '톈진시', zh: '天津市' }, center: [39.1, 117.2] },
    { id: 'Inner Mongolia', name: { en: 'Inner Mongolia', ja: '内モンゴル自治区', ko: '내몽골 자치구', zh: '内蒙古自治区' }, center: [43.6, 116.0] },
    { id: 'Shanxi', name: { en: 'Shanxi', ja: '山西省', ko: '산시성', zh: '山西省' }, center: [38.0, 112.5] },
    { id: 'Shaanxi', name: { en: 'Shaanxi', ja: '陝西省', ko: '섬서성', zh: '陕西省' }, center: [34.3, 108.9] },
    { id: 'Henan', name: { en: 'Henan', ja: '河南省', ko: '허난성', zh: '河南省' }, center: [34.0, 113.5] },
    { id: 'Gansu', name: { en: 'Gansu', ja: '甘粛省', ko: '간쑤성', zh: '甘肃省' }, center: [36.0, 103.8] },
    { id: 'Qinghai', name: { en: 'Qinghai', ja: '青海省', ko: '칭하이성', zh: '青海省' }, center: [36.6, 101.8] },
    { id: 'Guizhou', name: { en: 'Guizhou', ja: '貴州省', ko: '구이저우성', zh: '贵州省' }, center: [26.6, 106.7] },
    { id: 'Hubei', name: { en: 'Hubei', ja: '湖北省', ko: '후베이성', zh: '湖北省' }, center: [31.0, 112.2] },
    { id: 'Xinjiang', name: { en: 'Xinjiang', ja: '新疆ウイグル自治区', ko: '신장 위구르 자치구', zh: '新疆维吾尔自治区' }, center: [44.0, 87.5] },
    { id: 'Sichuan', name: { en: 'Sichuan', ja: '四川省', ko: '쓰촨성', zh: '四川省' }, center: [30.6, 103.0] },
    { id: 'Yunnan', name: { en: 'Yunnan', ja: '雲南省', ko: '윈난성', zh: '云南省' }, center: [26.9, 100.2] },
    { id: 'Zhejiang', name: { en: 'Zhejiang', ja: '浙江省', ko: '저장성', zh: '浙江省' }, center: [30.3, 120.2] },
    { id: 'Guangdong', name: { en: 'Guangdong', ja: '広東省', ko: '광둥성', zh: '广东省' }, center: [23.1, 113.3] },
    { id: 'Shanghai', name: { en: 'Shanghai', ja: '上海市', ko: '상하이시', zh: '上海市' }, center: [31.2, 121.5] },
  ],
  regionAliases: {
    'chongli': 'Hebei',
    'zhangjiakou': 'Hebei',
    'altay': 'Xinjiang',
  },
  map: { center: [40.5, 118.0], zoom: 5, bounds: [[18.0, 73.5], [53.6, 135.1]] },
  analytics: {
    ga4Id: 'G-DP05ZVPMHF',
    adsenseClient: 'ca-pub-3385716674270944',
    searchConsoleVerification: [],
  },
  // Shared Lovable Cloud project (snow-alert sign-ups) until the site gets its own.
  supabase: {
    url: 'https://koqicactsetakozoahvh.supabase.co',
    publishableKey: 'sb_publishable_4vvTdgjilt8UUK7xYw6-WQ_FCkNtpho',
    projectId: 'koqicactsetakozoahvh',
  },
  originCity: { en: 'Beijing', ja: '北京', ko: '베이징', zh: '北京' },
  railBrand: { en: 'High-speed rail', ja: '高速鉄道', ko: '고속철도', zh: '高铁' },
  transportOrigins: [
    { id: 'beijing', name: { en: 'Beijing', ja: '北京', ko: '베이징', zh: '北京' }, coords: [39.9042, 116.4074], airports: ['PEK', 'PKX'] },
    { id: 'shanghai', name: { en: 'Shanghai', ja: '上海', ko: '상하이', zh: '上海' }, coords: [31.2304, 121.4737], airports: ['PVG', 'SHA'] },
    { id: 'harbin', name: { en: 'Harbin', ja: 'ハルビン', ko: '하얼빈', zh: '哈尔滨' }, coords: [45.8038, 126.535], airports: ['HRB'] },
    { id: 'urumqi', name: { en: 'Urumqi', ja: 'ウルムチ', ko: '우루무치', zh: '乌鲁木齐' }, coords: [43.8256, 87.6168], airports: ['URC'] },
    { id: 'hong-kong', name: { en: 'Hong Kong', ja: '香港', ko: '홍콩', zh: '香港' }, coords: [22.3193, 114.1694], airports: ['HKG'] },
  ],
  features: {
    // No Chinese resort has a public, embeddable webcam (see CAMERAS.md): resort pages show
    // conditions + NASA GIBS satellite snow cover, /webcams the overview (/live-cameras 301s to it).
    noCameraPanel: true,
    liveCameras: false,
    racetracks: false,
    x5Pass: false,
    muslimFriendly: false,
    skicast: false,
    currencyConverter: true,
    snowForecast: false,
    monthlySnowForecast: true,
    winterTraffic: true,
    seasonCalendar: true,
    bestResorts: true,
    snowReportSatelliteMap: true,
  },
  routes: {
    bestResorts: 'best-ski-resorts-china',
    flagship: { slug: 'chongli', resortSlug: 'wanlong' },
  },
  extraRoutes: [
    { path: 'china-travel-essentials', page: 'PracticalitiesPage' },
    { path: 'altay', page: 'AltayPage' },
    { path: 'harbin-ice-festival', page: 'HarbinIceFestivalPage' },
    // China's own transport guide replaces the Japan-written shared /winter-traffic page.
    { path: 'winter-traffic', page: 'CnWinterTrafficPage' },
    { path: 'opening-dates', page: 'CnOpeningDatesPage' },
    { path: 'season-calendar', page: 'CnSeasonCalendarPage' },
    { path: 'resorts', page: 'CnResortsPage' },
  ],
  gettingThere: { href: '/winter-traffic', label: { en: 'Getting there', ja: '行き方', ko: '가는 법', zh: '如何前往' } },
  crossSitePromos: [
    {
      site: 'jp',
      url: 'https://japansnowwatch.com/',
      name: { en: 'JapanSnowWatch', ja: 'JapanSnowWatch', ko: 'JapanSnowWatch', zh: 'JapanSnowWatch' },
      blurb: { en: 'Japanese ski resorts', ja: '日本のスキー場', ko: '일본 스키장', zh: '日本滑雪场' },
      cta: { en: 'Ski Japan', ja: '日本でスキー', ko: '일본에서 스키', zh: '去日本滑雪' },
      languages: ['en', 'ja', 'ko'],
    },
    {
      site: 'kr',
      url: 'https://koreasnowwatch.com/',
      name: { en: 'KoreaSnowWatch', ja: 'KoreaSnowWatch', ko: 'KoreaSnowWatch', zh: 'KoreaSnowWatch' },
      blurb: { en: 'Korean ski resorts', ja: '韓国のスキー場', ko: '한국 스키장', zh: '韩国滑雪场' },
      cta: { en: 'Ski Korea', ja: '韓国でスキー', ko: '한국에서 스키', zh: '去韩国滑雪' },
      languages: ['ko', 'en', 'ja', 'zh'],
    },
  ],
  storageKeys: {
    cookieConsent: 'csw-cookie-consent',
    language: 'chinasnow-language',
    currency: 'chinasnow-currency',
    rates: 'chinasnow-rates',
  },
  seo: {
    title: 'China Ski Resorts: Snow Forecast & Satellite Snow Cover',
    description: 'Snow forecasts, daily satellite snow cover, lift prices and trip guides for Chinese ski resorts, written for visitors from outside China.',
    keywords: 'china ski resort, ski china, china snow forecast, chongli, yabuli, changbaishan, altay skiing, 中国滑雪场, 滑雪场雪况, 중국 스키장, 中国 スキー場',
    ogTitle: 'China Ski Resorts: Snow Forecast, Satellite Snow Cover & Guides | ChinaSnowWatch',
    ogDescription: 'Snow forecasts, daily satellite snow cover, lift prices and trip guides for Chinese ski resorts.',
    twitterTitle: 'China Ski Resorts: Snow Forecast & Satellite Snow Cover',
    twitterDescription: 'Snow forecasts, daily satellite snow cover, lift prices and trip guides for Chinese ski resorts.',
    htmlLang: 'en',
    ogLocale: 'en_GB',
    ogLocaleAlternates: ['zh_CN', 'ko_KR', 'ja_JP'],
    shellHeading: 'China Ski Resorts: Snow Forecast, Satellite Snow Cover & Guides',
    shellParagraphs: [
      'Snow forecasts, snow depth and daily satellite snow-cover images for ski resorts across China, from Chongli near Beijing to Yabuli, Changbaishan and Altay.',
      'Chinese resorts stream their slopes on WeChat and Douyin rather than public webcams, so each resort page shows the latest satellite view of the snow next to temperature, wind and a seven-day snowfall forecast, plus lift prices, trains and visa notes for foreign visitors.',
    ],
    baseKeywords: 'china ski resort, ski china, china snow forecast, chongli, yabuli, changbaishan, altay, 中国滑雪场, 중국 스키장, 中国 スキー場',
    rootTitle: 'China Ski Resorts: Snow Forecast, Satellite Snow Cover & Guides',
    rootDescription: 'Snow forecasts, satellite snow cover and trip guides for ski resorts across China.',
    notFoundDescription:
      "The page you're looking for doesn't exist or has moved. Browse Chinese ski resorts, snow forecasts and trip guides below.",
  },
  popularResorts: ['yabuli', 'thaiwoo', 'wanlong', 'genting-secret-garden', 'beidahu', 'changbaishan-wanda', 'songhua-lake'],
  weeklyReportSlugs: ['china-snow-report'],
};

export default config;
